import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { navigationItems as defaultItems } from '../../config/navigationConfig';

const Navigation = ({
    closeMenu,
    collapsed = false,
    items = defaultItems,
    density = 'default',
    collapsibleSections = false,
}) => {
    const { t } = useTranslation();
    const location = useLocation();
    const [searchTerm, setSearchTerm] = useState('');
    const [sectionOpenState, setSectionOpenState] = useState({});

    const normalizedItems = useMemo(() => {
        if (Array.isArray(items) && items.length > 0 && items[0]?.items) {
            return items.map((section) => ({
                ...section,
                items: (section.items || []).filter(Boolean),
            }));
        }

        return [{ id: 'default', items: Array.isArray(items) ? items : [] }];
    }, [items]);

    // Filter out settings and profile - they're now in top bar dropdown
    const filteredSections = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();

        return normalizedItems
            .map((section) => {
                const sectionItems = (section.items || [])
                    .filter(item => item.path !== '/settings' && item.path !== '/profile')
                    .filter((item) => {
                        if (!q) return true;
                        const translated = t(item.labelKey || '') || '';
                        const fallback = item.label || '';
                        return `${translated} ${fallback}`.toLowerCase().includes(q);
                    });

                return {
                    ...section,
                    items: sectionItems,
                };
            })
            .filter((section) => section.items.length > 0);
    }, [normalizedItems, searchTerm, t]);

    // MATCHED LOGIC: Use startsWith to handle sub-routes (e.g. /campaigns/edit)
    const isActive = (path) => location.pathname === path || (path !== '/' && location.pathname.startsWith(`${path}/`));

    return (
        <NavList $density={density}>
            {!collapsed && (
                <SearchWrap $density={density}>
                    <SearchInput
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t('Common.search', { defaultValue: 'Search navigation' })}
                        aria-label={t('Common.search', { defaultValue: 'Search navigation' })}
                    />
                </SearchWrap>
            )}

            {filteredSections.map((section) => (
                <React.Fragment key={section.id || section.label || 'section'}>
                    {!collapsed && section.label && (
                        collapsibleSections ? (
                            <SectionToggle
                                type="button"
                                $density={density}
                                onClick={() => {
                                    const key = section.id || section.label || 'section';
                                    setSectionOpenState((prev) => ({ ...prev, [key]: !(prev[key] ?? true) }));
                                }}
                                aria-expanded={searchTerm.trim() ? true : (sectionOpenState[section.id || section.label || 'section'] ?? true)}
                            >
                                <SectionLabelText>
                                    {section.labelKey ? t(section.labelKey) : section.label}
                                </SectionLabelText>
                                <SectionChevron $open={searchTerm.trim() ? true : (sectionOpenState[section.id || section.label || 'section'] ?? true)}>
                                    ▾
                                </SectionChevron>
                            </SectionToggle>
                        ) : (
                            <SectionLabel $density={density}>
                                {section.labelKey ? t(section.labelKey) : section.label}
                            </SectionLabel>
                        )
                    )}
                    {(searchTerm.trim() ? section.items : ((sectionOpenState[section.id || section.label || 'section'] ?? true) ? section.items : [])).map((item) => (
                        <NavItem
                            key={item.path}
                            to={item.path}
                            $isActive={isActive(item.path)}
                            onClick={closeMenu}
                            $collapsed={collapsed}
                            $density={density}
                        >
                            {item.icon}
                            <NavItemLabel $collapsed={collapsed}>{t(item.labelKey)}</NavItemLabel>
                            {collapsed && <Tooltip>{t(item.labelKey)}</Tooltip>}
                        </NavItem>
                    ))}
                </React.Fragment>
            ))}
        </NavList>
    );
};

export default Navigation;

// Styled Components matches ManufacturerNavigation.js

const NavList = styled.ul`
    list-style: none;
    padding: ${({ $density }) => ($density === 'compact' ? '0.75rem 0' : '1.5rem 0')};
    margin: 0;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow-y: auto;
    gap: ${({ $density }) => ($density === 'compact' ? '0.125rem' : '0.25rem')};
    font-size: ${({ $density }) => ($density === 'compact' ? '0.9rem' : '0.93rem')};

    @media (max-width: 768px) {
        padding: 0;
        width: 100%;
        font-size: 1rem;
    }
`;

const SearchWrap = styled.li`
    list-style: none;
    padding: ${({ $density }) => ($density === 'compact' ? '0 0.625rem 0.375rem' : '0 0.75rem 0.5rem')};
    position: sticky;
    top: 0;
    background: ${({ theme }) => theme.colors.sidebarBg || '#fff'};
    z-index: 1;

    @media (max-width: 768px) {
        padding: 0.625rem 0.75rem;
        background: transparent;
    }
`;

const SearchInput = styled.input`
    width: 100%;
    border: 1px solid ${({ theme }) => theme.colors.border || '#e5e7eb'};
    background: ${({ theme }) => theme.colors.surface || '#fff'};
    color: ${({ theme }) => theme.colors.textPrimary || '#111827'};
    border-radius: 10px;
    padding: 0.5625rem 0.75rem;
    font-size: 0.875rem;
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &::placeholder {
        color: ${({ theme }) => theme.colors.textMuted || '#9ca3af'};
    }

    &:focus {
        border-color: var(--color-primary);
        box-shadow: 0 0 0 3px var(--color-primary-bg);
    }
`;

const SectionLabel = styled.li`
    list-style: none;
    margin: ${({ $density }) => ($density === 'compact' ? '0.5rem 0.875rem 0.125rem' : '0.625rem 1rem 0.25rem')};
    padding: 0 0.75rem;
    color: ${({ theme }) => theme.colors.textMuted || '#6b7280'};
    font-size: ${({ $density }) => ($density === 'compact' ? '0.68rem' : '0.72rem')};
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`;

const SectionToggle = styled.button`
    list-style: none;
    margin: ${({ $density }) => ($density === 'compact' ? '0.5rem 0.625rem 0.125rem' : '0.625rem 0.75rem 0.25rem')};
    padding: ${({ $density }) => ($density === 'compact' ? '0.25rem 0.625rem' : '0.25rem 0.75rem')};
    width: calc(100% - ${({ $density }) => ($density === 'compact' ? '20px' : '24px')});
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: none;
    background: transparent;
    cursor: pointer;
    border-radius: 8px;
    transition: background-color 0.15s ease;

    &:hover {
        background: ${({ theme }) => theme.colors.surfaceAlt || '#f9fafb'};
    }
`;

const SectionLabelText = styled.span`
    color: ${({ theme }) => theme.colors.textMuted || '#6b7280'};
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`;

const SectionChevron = styled.span`
    color: ${({ theme }) => theme.colors.textMuted || '#6b7280'};
    font-size: 0.875rem;
    line-height: 1;
    transform: rotate(${({ $open }) => ($open ? '0deg' : '-90deg')});
    transition: transform 0.15s ease;
`;

const Tooltip = styled.div`
    position: absolute;
    left: 100%;
    top: 50%;
    transform: translateY(-50%) translateX(10px) scale(0.9);
    background-color: ${({ theme }) => theme.colors.tooltipBg || '#374151'}; /* Dark gray for high contrast */
    color: white;
    padding: 0.375rem 0.75rem;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
    transition: all 0.2s cubic-bezier(0.2, 0.8, 0.2, 1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 9999;

    /* Little triangle pointer */
    &::before {
        content: '';
        position: absolute;
        left: -4px;
        top: 50%;
        transform: translateY(-50%);
        border-width: 4px 6px 4px 0;
        border-style: solid;
        border-color: transparent ${({ theme }) => theme.colors.tooltipBg || '#374151'} transparent transparent;
    }
`;

const NavItem = styled(Link)`
    display: flex;
    align-items: center;
    color: ${({ theme, $isActive }) => ($isActive ? 'var(--color-primary)' : (theme.colors.navText || '#4b5563'))};
    text-decoration: none;
    padding: ${({ $density }) => ($density === 'compact' ? '0.5rem 0.875rem' : '0.625rem 1rem')};
    margin: ${({ $density }) => ($density === 'compact' ? '0.125rem 0.625rem' : '0.25rem 0.75rem')};
    position: relative;
    transition: all 0.2s ease-out;
    background: ${({ theme, $isActive }) => ($isActive ? 'var(--color-primary-bg)' : (theme.colors.navItemBg || 'transparent'))};
    width: auto;
    border-radius: ${({ $density }) => ($density === 'compact' ? '10px' : '12px')};
    justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'flex-start')};
    font-weight: ${({ $isActive }) => ($isActive ? '600' : '500')};
    letter-spacing: -0.01em;

    /* The Floating Pill Accent */
    &::before {
        content: '';
        position: absolute;
        left: -12px;
        top: 0;
        bottom: 0;
        transform: scaleY(${({ $isActive }) => ($isActive ? 1 : 0)});
        transform-origin: center;
        height: auto;
        width: 4px;
        background-color: var(--color-primary);
        border-radius: 0 4px 4px 0;
        opacity: ${({ $isActive }) => ($isActive ? 1 : 0)};
        transition: transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.2s ease;
    }

    &:hover {
        background-color: ${({ theme, $isActive }) => ($isActive ? (theme.colors.navItemActiveBgHover || 'rgba(130, 22, 146, 0.12)') : 'var(--color-primary-bg-soft)')}; /* slightly richer hover */
        color: var(--color-primary);

        /* Subtle scale for distinct tactile feel */
        transform: translateX(${({ $collapsed }) => ($collapsed ? '0' : '3px')}); /* Don't move if collapsed */

        &::before {
            opacity: ${({ $isActive }) => ($isActive ? 1 : 0.4)};
            transform: scaleY(1);
        }

        .icon {
            color: var(--color-primary);
            transform: scale(${({ $density }) => ($density === 'compact' ? 1.06 : 1.1)}); /* Subtle icon grow */
        }

        ${Tooltip} {
            opacity: 1;
            transform: translateY(-50%) translateX(10px) scale(1);
        }
    }

    /* Remove the old full-box ghosting in favor of the refined background above */
    &::after {
        display: none;
    }

    .icon {
        margin-right: ${({ $collapsed, $density }) => ($collapsed ? '0' : ($density === 'compact' ? '0.75rem' : '0.875rem'))};
        /* Ikone su do sada nosile size={24} u navigationConfig, sto postavlja
           width/height na samom svg-u i gazi ovaj font-size - pa gustoca nije
           mijenjala nista, samo je izgledalo kao da mijenja. Bez tog atributa
           svg pada na 1em i konacno prati ovaj redak. */
        font-size: ${({ $density }) => ($density === 'compact' ? '1.125rem' : '1.25rem')};
        opacity: 1;
        color: ${({ theme, $isActive }) => ($isActive ? 'inherit' : (theme.colors.navIconMuted || '#9ca3af'))};
        flex-shrink: 0;
        transition: all 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
    }

    @media (max-width: 768px) {
        margin: 0;
        border-radius: 0;
        padding: 1.25rem 1.5rem;

        &::before {
            left: 0;
            border-radius: 0 4px 4px 0;
            top: 0;
            bottom: 0;
            transform: scaleY(${({ $isActive }) => ($isActive ? 1 : 0)});
        }
    }
`;

const NavItemLabel = styled.span`
    overflow: hidden;
    white-space: nowrap;
    opacity: ${({ $collapsed }) => ($collapsed ? 0 : 1)};
    max-width: ${({ $collapsed }) => ($collapsed ? '0' : '200px')}; /* Animate max-width for slide effect */
    transition: opacity 0.2s ease, max-width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    letter-spacing: 0.01em;
`;
